import {
    Breakpoint,
    domain,
    getRecentSearches,
    LocationOption,
    MarketResearchSearchCriteria,
    MarketResearchShareDialog,
    saveRecentSearch,
    telemetryService,
    useAsyncEffect,
    useRouter,
} from "@relode/core";
import qs from "qs";
import * as React from "react";

import {
    MarketResearchHelpDialog,
    MarketResearchHelpSection,
} from "../../shared/components";
import {
    ClientMarketResearchProvider,
    useClientMarketResearchContext,
} from "./_infra/clientMarketResearchProvider";
import { ClientMarketResearchDesktop } from "./clientMarketResearchDesktop";
import { ClientMarketResearchMobile } from "./clientMarketResearchMobile";

export const ClientMarketResearchContainer = () => (
    <ClientMarketResearchProvider>
        <ClientMarketResearchInnerContainer />
    </ClientMarketResearchProvider>
);

const ClientMarketResearchInnerContainer = () => {
    const { history } = useRouter();
    const [state, actions] = useClientMarketResearchContext();

    const {
        searchCriteria,
        searchResults: {
            share: { data: shareData },
            summary: { data: summaryData },
            compensation: { data: compensationData },
        },
        jobManager: { data: jobManagerData },
    } = state;

    //initialize configuration data with the data pulled from the url path
    const [
        internalMarketResearchSearchCriteria,
        setInternalMarketResearchSearchCriteria,
    ] = React.useState<MarketResearchSearchCriteria>();
    const [shareDialogOpen, setShareDialogOpen] = React.useState(() => false);
    const [helpDialogOpen, setHelpDialogOpen] = React.useState(() => false);
    const [recentSearches, setRecentSearches] = React.useState<
        MarketResearchSearchCriteria[]
    >([]);

    useAsyncEffect(async () => {
        // always set internal criteria (for resetting)
        setInternalMarketResearchSearchCriteria(searchCriteria);

        // when we have criteria, let's load the data
        if (!!searchCriteria) {
            telemetryService.trackEvent(
                "MarketResearch-configured-private-tool-search",
                searchCriteria
            );

            await actions.loadMarketResearch();
        }
    }, [searchCriteria]);

    // load from storage any recent searches
    React.useEffect(() => {
        setRecentSearches(getRecentSearches());
    }, []);

    React.useEffect(() => {
        // when we have new criteria, save it to recent
        if (!!searchCriteria) {
            saveRecentSearch(searchCriteria);
            setRecentSearches(getRecentSearches());
        }
    }, [searchCriteria]);

    const handleJobTitleChange = (
        item?: never,
        normalizedJobTitle?: domain.NormalizedJobTitle
    ) => {
        const state = {
            ...internalMarketResearchSearchCriteria,
            jobName: normalizedJobTitle?.jobName,
        };
        setInternalMarketResearchSearchCriteria(state);
    };

    const handleLocationFilter = (location: LocationOption) => {
        const state = {
            ...internalMarketResearchSearchCriteria,
            locationId: location?.locationId,
            locationName: location?.locationName,
        };

        setInternalMarketResearchSearchCriteria(state);
    };

    const handleSearch = () => {
        // drive everything from querystring so just push new params onto history
        const queryString = qs.stringify(internalMarketResearchSearchCriteria, {
            skipNulls: true,
            addQueryPrefix: true,
            sort: (a, b) => a.localeCompare(b),
        });

        history.push({ search: queryString });
    };

    const handleMarketResearchShareOpen = () => {
        setShareDialogOpen(true);
    };

    const handleMarketResearchShareClose = () => {
        setShareDialogOpen(false);
    };

    const handleMarketResearchHelpOpen = () => {
        setHelpDialogOpen(true);
    };

    const handleMarketResearchHelpClose = () => {
        setHelpDialogOpen(false);
    };

    const estimatedAnnualSalary =
        compensationData?.annualJobSalaryInUSDollars ?? 0;

    return (
        <>
            <Breakpoint desktop>
                <ClientMarketResearchDesktop
                    onShare={handleMarketResearchShareOpen}
                    onSearch={handleSearch}
                    onLocationChange={handleLocationFilter}
                    onJobTitleChange={handleJobTitleChange}
                    onHelp={handleMarketResearchHelpOpen}
                    searchCriteria={internalMarketResearchSearchCriteria}
                    recentSearches={recentSearches}
                    helpSection={
                        <MarketResearchHelpSection
                            jobName={
                                internalMarketResearchSearchCriteria?.jobName
                            }
                            locationName={
                                internalMarketResearchSearchCriteria?.locationName
                            }
                            annualSalary={estimatedAnnualSalary}
                            jobManager={jobManagerData}
                        />
                    }
                />
            </Breakpoint>
            <Breakpoint mobile>
                <ClientMarketResearchMobile
                    onShare={handleMarketResearchShareOpen}
                    onSearch={handleSearch}
                    onLocationChange={handleLocationFilter}
                    onJobTitleChange={handleJobTitleChange}
                    onHelp={handleMarketResearchHelpOpen}
                    searchCriteria={internalMarketResearchSearchCriteria}
                    recentSearches={recentSearches}
                    helpSection={
                        <MarketResearchHelpSection
                            jobName={
                                internalMarketResearchSearchCriteria?.jobName
                            }
                            locationName={
                                internalMarketResearchSearchCriteria?.locationName
                            }
                            annualSalary={estimatedAnnualSalary}
                            jobManager={jobManagerData}
                        />
                    }
                />
            </Breakpoint>
            {/* Summary data means valid and share data has URLs */}
            {!!shareData && !!summaryData && shareDialogOpen ? (
                <MarketResearchShareDialog
                    jobName={internalMarketResearchSearchCriteria?.jobName}
                    locationName={
                        internalMarketResearchSearchCriteria?.locationName
                    }
                    emailUrl={shareData.emailUrl}
                    shortenedUrl={shareData.shortenedUrl}
                    open={shareDialogOpen}
                    onClose={handleMarketResearchShareClose}
                />
            ) : null}
            {!!jobManagerData && !!summaryData && helpDialogOpen ? (
                <MarketResearchHelpDialog
                    jobName={internalMarketResearchSearchCriteria?.jobName}
                    locationName={
                        internalMarketResearchSearchCriteria?.locationName
                    }
                    annualSalary={estimatedAnnualSalary}
                    jobManager={jobManagerData}
                    open={helpDialogOpen}
                    onClose={handleMarketResearchHelpClose}
                />
            ) : null}
        </>
    );
};
